import { gql } from '@apollo/client';
import { apolloClient } from '../apollo/client';
import type {
  ResumoFinanceiro,
  PontoSerieFinanceira,
  QuebraFinanceira,
  PeriodoFinanceiro,
  ExtratoFinanceiroConnection,
  TipoExtratoLinha,
  CustoCampanhaCupom,
} from '../types/financeiro';

export type { ResumoFinanceiro, PontoSerieFinanceira, QuebraFinanceira, PeriodoFinanceiro, CustoCampanhaCupom };

// Requisitos não-negociáveis (Task 2 do plano):
// 1. Toda query é NOMEADA — sem nome, o matching de mock por operação não funciona.
// 2. Nenhum `__typename` no documento — test-utils.tsx monta o MockedProvider com
//    addTypename={false}, e pedir `__typename` faria todo mock não bater.
// 3. Nenhum parâmetro de identificação (atletaId/usuarioId) — o recorte vem do token (guard S-1).

export const MEU_RESUMO_FINANCEIRO = gql`
  query MeuResumoFinanceiro($filtro: FiltroFinanceiroInput!) {
    meuResumoFinanceiro(filtro: $filtro) {
      totalRecebido
      totalEmAberto
      totalNaoRealizado
      totalEstornado
      quantidadeGratuita
      totalTaxaPlataforma
      totalRepasseLiga
    }
  }
`;

export const MINHA_SERIE_POR_DIA = gql`
  query MinhaSeriePorDia($filtro: FiltroFinanceiroInput!) {
    minhaSeriePorDia(filtro: $filtro) {
      data
      valor
    }
  }
`;

export const MINHA_QUEBRA_POR_CATEGORIA = gql`
  query MinhaQuebraPorCategoria($filtro: FiltroFinanceiroInput!) {
    minhaQuebraPorCategoria(filtro: $filtro) {
      categoriaTorneioId
      categoriaNome
      valor
    }
  }
`;

// financeiro-dashboard-atleta Phase 4 (Task 6) — extrato unificado, com os dois argumentos
// novos desta fase (`status`, além de `tipos` já existente da Phase 2).
export const MEU_EXTRATO_FINANCEIRO = gql`
  query MeuExtratoFinanceiro($filtro: FiltroFinanceiroInput!, $tipos: [TipoExtratoLinha!], $status: [String!], $page: Int, $size: Int) {
    meuExtratoFinanceiro(filtro: $filtro, tipos: $tipos, status: $status, page: $page, size: $size) {
      items { tipo data vencimento descricao valor status origem referenciaId reciboUrl }
      page size totalItems totalPages
    }
  }
`;

export interface MeuResumoFinanceiroData {
  meuResumoFinanceiro: ResumoFinanceiro | null;
}

export interface MeuExtratoFinanceiroData {
  meuExtratoFinanceiro: ExtratoFinanceiroConnection | null;
}

export interface MeuExtratoFinanceiroVars {
  filtro: PeriodoFinanceiro;
  tipos: TipoExtratoLinha[] | null;
  status: string[] | null;
  page: number;
  size: number;
}

export interface MinhaSeriePorDiaData {
  minhaSeriePorDia: PontoSerieFinanceira[];
}

export interface MinhaQuebraPorCategoriaData {
  minhaQuebraPorCategoria: QuebraFinanceira[];
}

export interface FinanceiroFiltroVars {
  filtro: PeriodoFinanceiro;
}

// financeiro-dashboard-liga-torneio Phase 3 (Task 1/Task 2) — contrato observado em
// inplay-back/src/main/resources/graphql/financeiro.graphqls:79-90 (2026-08-11):
//   resumoFinanceiroLiga(ligaId: ID!, filtro: FiltroFinanceiroInput!): ResumoFinanceiro
//   seriePorDiaLiga(ligaId: ID!, filtro: FiltroFinanceiroInput!): [PontoSerieFinanceira!]
//   quebraPorCategoriaLiga(ligaId: ID!, filtro: FiltroFinanceiroInput!): [QuebraFinanceira!]
// Ao contrário do trio "meu*" (atleta), o recorte da liga NÃO vem do token — `ligaId` é
// argumento explícito e obrigatório (`ID!`), então cada documento abaixo declara `$ligaId`
// além de `$filtro`. Campos de ResumoFinanceiro idênticos ao trio do atleta, incluindo
// totalTaxaPlataforma/totalRepasseLiga (B-11: visíveis ao organizador).
export const RESUMO_FINANCEIRO_LIGA = gql`
  query ResumoFinanceiroLiga($ligaId: ID!, $filtro: FiltroFinanceiroInput!) {
    resumoFinanceiroLiga(ligaId: $ligaId, filtro: $filtro) {
      totalRecebido
      totalEmAberto
      totalNaoRealizado
      totalEstornado
      quantidadeGratuita
      totalTaxaPlataforma
      totalRepasseLiga
    }
  }
`;

export const SERIE_POR_DIA_LIGA = gql`
  query SeriePorDiaLiga($ligaId: ID!, $filtro: FiltroFinanceiroInput!) {
    seriePorDiaLiga(ligaId: $ligaId, filtro: $filtro) {
      data
      valor
    }
  }
`;

export const QUEBRA_POR_CATEGORIA_LIGA = gql`
  query QuebraPorCategoriaLiga($ligaId: ID!, $filtro: FiltroFinanceiroInput!) {
    quebraPorCategoriaLiga(ligaId: $ligaId, filtro: $filtro) {
      categoriaTorneioId
      categoriaNome
      valor
    }
  }
`;

export interface ResumoFinanceiroLigaData {
  resumoFinanceiroLiga: ResumoFinanceiro | null;
}

export interface SeriePorDiaLigaData {
  seriePorDiaLiga: PontoSerieFinanceira[];
}

export interface QuebraPorCategoriaLigaData {
  quebraPorCategoriaLiga: QuebraFinanceira[];
}

export interface FiltroLigaVars {
  ligaId: string;
  filtro: PeriodoFinanceiro;
}

// financeiro-dashboard-liga-torneio Phase 5 (AC-A2/AC-A3, desvio de escopo aprovado pelo owner em
// 2026-08-11): recorte por torneio, mesma convenção do trio de liga acima, trocando `$ligaId` por
// `$torneioId`. Campos idênticos ao trio de liga -- os tipos de retorno (`ResumoFinanceiro`,
// `QuebraFinanceira`, `PontoSerieFinanceira`) são os mesmos, sem mudança de forma.
export const RESUMO_FINANCEIRO_TORNEIO = gql`
  query ResumoFinanceiroTorneio($torneioId: ID!, $filtro: FiltroFinanceiroInput!) {
    resumoFinanceiroTorneio(torneioId: $torneioId, filtro: $filtro) {
      totalRecebido
      totalEmAberto
      totalNaoRealizado
      totalEstornado
      quantidadeGratuita
      totalTaxaPlataforma
      totalRepasseLiga
    }
  }
`;

export const SERIE_POR_DIA_TORNEIO = gql`
  query SeriePorDiaTorneio($torneioId: ID!, $filtro: FiltroFinanceiroInput!) {
    seriePorDiaTorneio(torneioId: $torneioId, filtro: $filtro) {
      data
      valor
    }
  }
`;

export const QUEBRA_POR_CATEGORIA_TORNEIO = gql`
  query QuebraPorCategoriaTorneio($torneioId: ID!, $filtro: FiltroFinanceiroInput!) {
    quebraPorCategoriaTorneio(torneioId: $torneioId, filtro: $filtro) {
      categoriaTorneioId
      categoriaNome
      valor
    }
  }
`;

export interface ResumoFinanceiroTorneioData {
  resumoFinanceiroTorneio: ResumoFinanceiro | null;
}

export interface SeriePorDiaTorneioData {
  seriePorDiaTorneio: PontoSerieFinanceira[];
}

export interface QuebraPorCategoriaTorneioData {
  quebraPorCategoriaTorneio: QuebraFinanceira[];
}

export interface FiltroTorneioVars {
  torneioId: string;
  filtro: PeriodoFinanceiro;
}

// Contrato observado (attempt 3, R-SEM — ROOT CAUSE corrigido) para a query de Recebimentos:
//   pagamentosInscricaoFiltrados(atletaId: ID, categoriaTorneioId: ID, torneioId: ID, ligaId: ID,
//     status: StatusPagamento, inicio: Date, fim: Date, nomeAtleta: String,
//     sortBy: OrdenacaoPagamento, direction: DirecaoOrdenacao, page: Int = 0, size: Int = 10):
//     PagamentoInscricaoConnection
// (inplay-back/src/main/resources/graphql/schema.graphqls:226). Esta é a query que devolve
// PAGAMENTOS RECEBIDOS DOS ATLETAS (dinheiro atleta → liga) — o sentido correto para a aba
// Recebimentos (Solution Statement item 3 / AC-A4, PRD AC-9). A tentativa anterior apontava para
// `pagamentosInscritosLigaFiltrados` (schema.graphqls:317), que devolve `PagamentoInscritosLiga`
// — os BOLETOS QUE A LIGA DEVE À INPLAY (dinheiro liga → InPlay), sem atleta e sem `nomeAtleta` —
// daí o filtro de Atleta morto e o campo `descricao` inventado das tentativas 1/2. Essa query
// segue existindo e é a fonte correta para a aba "A pagar à InPlay" (ver APagarInPlayTab.tsx).
// Campos selecionados abaixo espelham `payments/LigaRecebimentosTab.tsx:339-357` (a aba que este
// hub substitui), o consumidor real mais recente do mesmo tipo `PagamentoInscricao`.
export const PAGAMENTOS_INSCRICAO_FILTRADOS_LIGA = gql`
  query PagamentosInscricaoFiltradosLiga(
    $ligaId: ID
    $categoriaTorneioId: ID
    $status: StatusPagamento
    $inicio: Date
    $fim: Date
    $nomeAtleta: String
    $sortBy: OrdenacaoPagamento
    $direction: DirecaoOrdenacao
    $page: Int
    $size: Int
  ) {
    pagamentosInscricaoFiltrados(
      ligaId: $ligaId
      categoriaTorneioId: $categoriaTorneioId
      status: $status
      inicio: $inicio
      fim: $fim
      nomeAtleta: $nomeAtleta
      sortBy: $sortBy
      direction: $direction
      page: $page
      size: $size
    ) {
      items {
        id
        valor
        status
        origemConfirmacao
        dataPagamento
        criadoEm
        inscricoes {
          id
          categoriaTorneio {
            id
            nome
          }
          equipe {
            atletas {
              id
              usuario {
                nome
              }
            }
          }
        }
      }
      page
      size
      totalItems
      totalPages
    }
  }
`;

// Contrato observado (attempt 4, R-SEM/R-COH-TASK-CONTRADICTION — fix da aba "A pagar à
// InPlay") para a query dos boletos que a LIGA deve à InPlay:
//   pagamentosInscritosLigaFiltrados(ligaId: ID, categoriaTorneioId: ID, status: StatusPagamento,
//     inicio: Date, fim: Date, sortBy: OrdenacaoPagamento, direction: DirecaoOrdenacao,
//     page: Int = 0, size: Int = 10): PagamentoInscritosLigaConnection
// (inplay-back/src/main/resources/graphql/schema.graphqls:317). Deliberadamente SEM `nomeAtleta`
// — um boleto de PagamentoInscritosLiga não tem atleta (é um lote por categoria, ver
// pagamentoInscritosLiga.graphqls:1-26). Campos selecionados abaixo existem literalmente em
// `type PagamentoInscritosLiga` (pagamentoInscritosLiga.graphqls) — nenhum campo inventado.
export const PAGAMENTOS_INSCRITOS_LIGA_FILTRADOS = gql`
  query PagamentosInscritosLigaFiltrados(
    $ligaId: ID
    $categoriaTorneioId: ID
    $status: StatusPagamento
    $inicio: Date
    $fim: Date
    $sortBy: OrdenacaoPagamento
    $direction: DirecaoOrdenacao
    $page: Int
    $size: Int
  ) {
    pagamentosInscritosLigaFiltrados(
      ligaId: $ligaId
      categoriaTorneioId: $categoriaTorneioId
      status: $status
      inicio: $inicio
      fim: $fim
      sortBy: $sortBy
      direction: $direction
      page: $page
      size: $size
    ) {
      items {
        id
        valor
        quantidadeInscritos
        status
        origemConfirmacao
        dataVencimento
        dataPagamento
        criadoEm
        categoriaTorneio {
          id
          nome
        }
      }
      page
      size
      totalItems
      totalPages
    }
  }
`;

// financeiro-dashboard-plataforma Phase 4 (Task 1/Task 3) — contrato observado em
// inplay-back/src/main/resources/graphql/financeiro.graphqls (2026-08-13), as 7 queries do
// perfil de plataforma:
//   resumoFinanceiroPlataforma(filtro: FiltroFinanceiroInput!): ResumoFinanceiro
//   quebraPorCategoriaPlataforma(torneioId: ID, filtro: FiltroFinanceiroInput!): [QuebraFinanceira!]
//   seriePorDiaPlataforma(filtro: FiltroFinanceiroInput!): [PontoSerieFinanceira!]
//   quebraPorLigaPlataforma(filtro: FiltroFinanceiroInput!): [QuebraFinanceira!]
//   quebraPorTorneioPlataforma(ligaId: ID, filtro: FiltroFinanceiroInput!): [QuebraFinanceira!]
//   quebraPorMeioPagamentoPlataforma(filtro: FiltroFinanceiroInput!): [QuebraFinanceira!]
//   custoCampanhasPlataforma(filtro: FiltroFinanceiroInput!): [CustoCampanhaCupom!]
// `torneioId` (quebraPorCategoriaPlataforma) e `ligaId` (quebraPorTorneioPlataforma) sao os
// DOIS UNICOS argumentos opcionais deste bloco — servem o drill-down liga → torneio →
// categoria da Phase 4. RESUMO_FINANCEIRO_PLATAFORMA seleciona os 9 campos completos de
// ResumoFinanceiro (incluindo totalReceitaBoletosLiga/totalReceitaInPlay); as 4 queries de
// QuebraFinanceira selecionam os 5 campos completos (chaveId/chaveNome + os 2 aliases legados),
// nunca só um alias.
export const RESUMO_FINANCEIRO_PLATAFORMA = gql`
  query ResumoFinanceiroPlataforma($filtro: FiltroFinanceiroInput!) {
    resumoFinanceiroPlataforma(filtro: $filtro) {
      totalRecebido
      totalEmAberto
      totalNaoRealizado
      totalEstornado
      quantidadeGratuita
      totalTaxaPlataforma
      totalRepasseLiga
      totalReceitaBoletosLiga
      totalReceitaInPlay
    }
  }
`;

export const SERIE_POR_DIA_PLATAFORMA = gql`
  query SeriePorDiaPlataforma($filtro: FiltroFinanceiroInput!) {
    seriePorDiaPlataforma(filtro: $filtro) {
      data
      valor
    }
  }
`;

export const QUEBRA_POR_CATEGORIA_PLATAFORMA = gql`
  query QuebraPorCategoriaPlataforma($torneioId: ID, $filtro: FiltroFinanceiroInput!) {
    quebraPorCategoriaPlataforma(torneioId: $torneioId, filtro: $filtro) {
      chaveId
      chaveNome
      categoriaTorneioId
      categoriaNome
      valor
    }
  }
`;

export const QUEBRA_POR_LIGA_PLATAFORMA = gql`
  query QuebraPorLigaPlataforma($filtro: FiltroFinanceiroInput!) {
    quebraPorLigaPlataforma(filtro: $filtro) {
      chaveId
      chaveNome
      categoriaTorneioId
      categoriaNome
      valor
    }
  }
`;

export const QUEBRA_POR_TORNEIO_PLATAFORMA = gql`
  query QuebraPorTorneioPlataforma($ligaId: ID, $filtro: FiltroFinanceiroInput!) {
    quebraPorTorneioPlataforma(ligaId: $ligaId, filtro: $filtro) {
      chaveId
      chaveNome
      categoriaTorneioId
      categoriaNome
      valor
    }
  }
`;

export const QUEBRA_POR_MEIO_PAGAMENTO_PLATAFORMA = gql`
  query QuebraPorMeioPagamentoPlataforma($filtro: FiltroFinanceiroInput!) {
    quebraPorMeioPagamentoPlataforma(filtro: $filtro) {
      chaveId
      chaveNome
      categoriaTorneioId
      categoriaNome
      valor
    }
  }
`;

export const CUSTO_CAMPANHAS_PLATAFORMA = gql`
  query CustoCampanhasPlataforma($filtro: FiltroFinanceiroInput!) {
    custoCampanhasPlataforma(filtro: $filtro) {
      codigo
      usos
      totalDesconto
    }
  }
`;

export interface ResumoFinanceiroPlataformaData {
  resumoFinanceiroPlataforma: ResumoFinanceiro | null;
}

export interface SeriePorDiaPlataformaData {
  seriePorDiaPlataforma: PontoSerieFinanceira[];
}

export interface QuebraPorCategoriaPlataformaData {
  quebraPorCategoriaPlataforma: QuebraFinanceira[];
}

export interface QuebraPorCategoriaPlataformaVars {
  torneioId?: string;
  filtro: PeriodoFinanceiro;
}

export interface QuebraPorLigaPlataformaData {
  quebraPorLigaPlataforma: QuebraFinanceira[];
}

export interface QuebraPorTorneioPlataformaData {
  quebraPorTorneioPlataforma: QuebraFinanceira[];
}

export interface QuebraPorTorneioPlataformaVars {
  ligaId?: string;
  filtro: PeriodoFinanceiro;
}

export interface QuebraPorMeioPagamentoPlataformaData {
  quebraPorMeioPagamentoPlataforma: QuebraFinanceira[];
}

export interface CustoCampanhasPlataformaData {
  custoCampanhasPlataforma: CustoCampanhaCupom[];
}

// Funções imperativas finas, mirror de pagamentoInscricaoService.ts:337-348 — para quem
// precisar consumir estas queries fora de um componente React. O consumo desta fase
// (hooks/useFinanceiroAtleta.ts) usa useQuery com os documentos acima, não estas funções
// (ver Notes do plano: MockedProvider intercepta hooks do Apollo, não apolloClient.query
// imperativo).

export const buscarMeuResumoFinanceiro = async (
  filtro: PeriodoFinanceiro,
): Promise<ResumoFinanceiro | null> => {
  const { data } = await apolloClient.query<MeuResumoFinanceiroData, FinanceiroFiltroVars>({
    query: MEU_RESUMO_FINANCEIRO,
    variables: { filtro },
    fetchPolicy: 'no-cache',
  });
  return data.meuResumoFinanceiro;
};

export const buscarMinhaSeriePorDia = async (
  filtro: PeriodoFinanceiro,
): Promise<PontoSerieFinanceira[]> => {
  const { data } = await apolloClient.query<MinhaSeriePorDiaData, FinanceiroFiltroVars>({
    query: MINHA_SERIE_POR_DIA,
    variables: { filtro },
    fetchPolicy: 'no-cache',
  });
  return data.minhaSeriePorDia;
};

export const buscarMinhaQuebraPorCategoria = async (
  filtro: PeriodoFinanceiro,
): Promise<QuebraFinanceira[]> => {
  const { data } = await apolloClient.query<MinhaQuebraPorCategoriaData, FinanceiroFiltroVars>({
    query: MINHA_QUEBRA_POR_CATEGORIA,
    variables: { filtro },
    fetchPolicy: 'no-cache',
  });
  return data.minhaQuebraPorCategoria;
};
